import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
    Leaf, Sun, TrendingUp, ArrowRight, ArrowUpRight, Zap, Flame,
    Candy, IceCream, Coffee, CroissantIcon, Wine, Newspaper, Sandwich, Fish, Donut,
    ChevronsUpDown, PanelTopOpen, Sparkles, Quote, ShieldCheck, Truck, Clock,
    Ruler, Snowflake, BatteryCharging, Users, Euro, Award, MessageCircle,
    Volume2, VolumeX,
} from "lucide-react";
import ProfitCalculator from "@/components/ProfitCalculator";
import Counter from "@/components/Counter";
import CtaBanner from "@/components/CtaBanner";
import InlineCTA from "@/components/InlineCTA";
import Reveal from "@/components/Reveal";
import BrandMarquee from "@/components/BrandMarquee";
import { api } from "@/lib/api";
import { usePageContent, useContentList } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import { resolveIcon } from "@/lib/cmsHelpers";
import useSeo from "@/lib/useSeo";

const HERO_IMG_DEFAULT = "/brand/alfa-hero.jpg?v=2";
const ALFA_IMG_DEFAULT = "/brand/alfa-product.jpg?v=2";
const OMEGA_IMG_DEFAULT = "/brand/omega-product.jpg?v=2";
const TETO_IMG_DEFAULT = "/brand/teto-retratil.jpg?v=2";

/* 5 flagship business layouts — each with a real photo, curated equipment
   list and estimated daily margin. Clicking a card takes the user straight
   to the Simulador with the matching theme preset already applied. */
const FLAGSHIP_BUSINESSES = [
    {
        id: "cafes", label: "Cafés & takeaway", tema: "cafes",
        image_clean: "/brand/tema-cafes.jpg?v=2",
        image_branded: "/brand/tema-cafes-branded.jpg?v=2",
        brand_example: "Café Lisboa",
        pitch_pt: "Espresso + capuccino num centro histórico. Baixo espaço, alta rotatividade.",
        pitch_en: "Espresso + cappuccino in the historic centre. Low footprint, high turnover.",
        equip_pt: ["Máquina 2 grupos", "Moinho profissional", "Iluminação warm"],
        equip_en: ["2-group espresso", "Professional grinder", "Warm lighting"],
        margin: "€45 – €120",
        margin_hint_pt: "margem estimada/dia",
        margin_hint_en: "est. daily margin",
    },
    {
        id: "gelados", label: "Gelados artesanais", tema: "gelados",
        image_clean: "/brand/tema-gelados.jpg?v=2",
        image_branded: "/brand/tema-gelados-branded.jpg?v=2",
        brand_example: "Gelato Douro",
        pitch_pt: "Marginal, praia ou parque no Verão. 6 sabores em vitrine curva.",
        pitch_en: "Seafront, beach or park in summer. 6 flavours in a curved display.",
        equip_pt: ["Vitrine 6 cubas", "Bateria plus (10h)", "Congelador -18°C"],
        equip_en: ["6-tub display", "Plus battery (10h)", "-18°C freezer"],
        margin: "€60 – €150",
        margin_hint_pt: "margem estimada/dia",
        margin_hint_en: "est. daily margin",
    },
    {
        id: "cachorros", label: "Cachorros quentes", tema: "sanduiches",
        image_clean: "/brand/tema-cachorros.jpg?v=2",
        image_branded: "/brand/tema-cachorros-branded.jpg?v=2",
        brand_example: "Dog & Go",
        pitch_pt: "Eventos desportivos e concertos. Plancha inox e bateria pro.",
        pitch_en: "Sports events and concerts. Stainless plancha and pro battery.",
        equip_pt: ["Plancha inox 60cm", "Bateria pro (16h)", "Solar + gás"],
        equip_en: ["60cm plancha", "Pro battery (16h)", "Solar + gas"],
        margin: "€80 – €200",
        margin_hint_pt: "margem estimada/evento",
        margin_hint_en: "est. per event",
    },
    {
        id: "pipocas", label: "Pipocas gourmet", tema: "doces",
        image_clean: "/brand/tema-pipocas.jpg?v=2",
        image_branded: "/brand/tema-pipocas-branded.jpg?v=2",
        brand_example: "Pop Cinema",
        pitch_pt: "Cinema ao ar livre, feiras e festivais. Investimento reduzido.",
        pitch_en: "Outdoor cinema, fairs and festivals. Low investment.",
        equip_pt: ["Máquina retro 8oz", "Iluminação neon", "Cones kraft"],
        equip_en: ["Retro 8oz machine", "Neon lighting", "Kraft cones"],
        margin: "€40 – €110",
        margin_hint_pt: "margem estimada/dia",
        margin_hint_en: "est. daily margin",
    },
    {
        id: "crepes", label: "Crepes & doces", tema: "doces",
        image_clean: "/brand/tema-crepes.jpg?v=2",
        image_branded: "/brand/tema-crepes-branded.jpg?v=2",
        brand_example: "Crepe Notte",
        pitch_pt: "Mercados de Natal e centros de cidade à noite. String-lights + neon.",
        pitch_en: "Christmas markets and city centres at night. String-lights + neon.",
        equip_pt: ["Placa crêpes 40cm", "Iluminação neon", "Bateria plus"],
        equip_en: ["40cm crêpe plate", "Neon lighting", "Plus battery"],
        margin: "€55 – €140",
        margin_hint_pt: "margem estimada/dia",
        margin_hint_en: "est. daily margin",
    },
];

/* Each theme has palette + primary equipment + short brand label displayed on the mini-cart */
const THEMES_FALLBACK = [
    { id: "doces", label: "Doces", brand: "SWEETS", icon: "Candy", primary: "#DB2777", accent: "#FBCFE8", soft: "#FDF2F8" },
    { id: "gelados", label: "Gelados", brand: "GELATO", icon: "IceCream", primary: "#0891B2", accent: "#A5F3FC", soft: "#ECFEFF" },
    { id: "churros", label: "Churros", brand: "CHURROS", icon: "Donut", primary: "#C2410C", accent: "#FED7AA", soft: "#FFF7ED" },
    { id: "cafes", label: "Cafés", brand: "CAFÉ", icon: "Coffee", primary: "#78350F", accent: "#FCD34D", soft: "#FFFBEB" },
    { id: "paes", label: "Pães", brand: "PADARIA", icon: "CroissantIcon", primary: "#92400E", accent: "#FDE68A", soft: "#FEFCE8" },
    { id: "drinks", label: "Drinks", brand: "BAR", icon: "Wine", primary: "#6B21A8", accent: "#E9D5FF", soft: "#FAF5FF" },
    { id: "jornais", label: "Jornais", brand: "NEWS", icon: "Newspaper", primary: "#1E3A5F", accent: "#DBEAFE", soft: "#EFF6FF" },
    { id: "sanduiches", label: "Sanduíches", brand: "DELI", icon: "Sandwich", primary: "#EA580C", accent: "#FED7AA", soft: "#FFF7ED" },
    { id: "sushis", label: "Sushis", brand: "SUSHI", icon: "Fish", primary: "#047857", accent: "#FECACA", soft: "#ECFDF5" },
    { id: "pipocas", label: "Pipocas", brand: "POP", icon: "Sparkles", primary: "#B45309", accent: "#FDE68A", soft: "#FFFBEB" },
    { id: "crepes", label: "Crepes", brand: "CRÊPE", icon: "CroissantIcon", primary: "#78350F", accent: "#FCD34D", soft: "#FFFBEB" },
];

const THEME_LABEL_EN = {
    doces: "Sweets", gelados: "Ice cream", churros: "Churros", cafes: "Coffee", paes: "Bakery",
    drinks: "Drinks", jornais: "News", sanduiches: "Sandwiches", sushis: "Sushi",
    pipocas: "Popcorn", crepes: "Crêpes",
};

const TETO_FEATURES_FALLBACK = [
    { icon: "ChevronsUpDown", title: "Teto Retrátil", desc: "Estrutura com perfis circulares e trava de segurança — fácil de dobrar, fácil de transportar." },
    { icon: "PanelTopOpen", title: "Topo Aberto", desc: "Tampo adicional de 55 cm que aumenta em 40% a área de serviço — ideal para pico de hora." },
    { icon: "Sparkles", title: "Personalização total", desc: "Cores, lettering e equipamento interno adaptados à identidade do seu negócio." },
];

const TETO_FEATURES_EN = [
    { icon: "ChevronsUpDown", title: "Retractable Roof", desc: "Circular-profile structure with safety lock — easy to fold, easy to transport." },
    { icon: "PanelTopOpen", title: "Open Top", desc: "Additional 55 cm counter that boosts service area by 40% — ideal for rush hour." },
    { icon: "Sparkles", title: "Full personalisation", desc: "Colours, lettering and internal equipment tailored to your business identity." },
];

const FUNCTIONAL_TILES_FALLBACK = [
    { icon: "Sun", title: "Painel Solar", desc: "Autonomia limpa e silenciosa durante todo o dia de trabalho." },
    { icon: "Zap", title: "Eletricidade", desc: "Ligação à rede quando precisar de reforço de potência." },
    { icon: "Flame", title: "Gás", desc: "Integração de botija para cozinhados e comida quente." },
];

const FUNCTIONAL_TILES_EN = [
    { icon: "Sun", title: "Solar Panel", desc: "Clean, silent autonomy throughout the working day." },
    { icon: "Zap", title: "Electricity", desc: "Grid connection whenever you need extra power." },
    { icon: "Flame", title: "Gas", desc: "Bottle integration for cooking and hot food." },
];

const TESTIMONIALS_FALLBACK = [
    {
        name: "Ana Ribeiro",
        role: "Proprietária · Café Solar, Porto",
        quote:
            "Em 4 meses recuperei o investimento no Alfa. A autonomia solar mudou completamente os meus custos operacionais.",
    },
    {
        name: "Tiago Mendes",
        role: "Heineken · Ativações Portugal",
        quote:
            "Precisávamos de algo mobile, elegante e fiável para festivais. A NOVIFOOD entregou exactamente isso, e a tempo.",
    },
    {
        name: "Marta Santos",
        role: "Franchise · Gelados Lisboa",
        quote:
            "O Ômega permite operação de duas pessoas em simultâneo. Fazemos o dobro da faturação em pico de verão.",
    },
];

export default function Home() {
    const [testimonials, setTestimonials] = useState(TESTIMONIALS_FALLBACK);
    const { t } = usePageContent("home");
    const { tr, en, fmtPrice } = useLocale();
    const themes = useContentList("home_themes", THEMES_FALLBACK);
    const cmsTeto = useContentList("home_teto_features", TETO_FEATURES_FALLBACK, { forceFallbackEn: false });
    const cmsFunctional = useContentList("home_functional_tiles", FUNCTIONAL_TILES_FALLBACK, { forceFallbackEn: false });
    const tetoFeatures = en ? TETO_FEATURES_EN : cmsTeto;
    const functionalTiles = en ? FUNCTIONAL_TILES_EN : cmsFunctional;

    useSeo({
        title: tr(
            "NOVIFOOD — Carrinhos premium de food cart à medida em Portugal",
            "NOVIFOOD — Premium custom food carts, made in Portugal"
        ),
        description: tr(
            "Carrinhos móveis premium para empreendedores e marcas. Configure o seu food cart com energia solar, equipamento profissional e branding personalizado. Entrega em 30 dias.",
            "Premium mobile food carts for entrepreneurs and brands. Configure your solar-powered food cart with professional equipment and custom branding. Delivered in 30 days."
        ),
        titleSuffix: false,
        path: "/",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "NOVIFOOD",
            "image": typeof window !== "undefined" ? `${window.location.origin}/brand/novifood-logo.png` : "/brand/novifood-logo.png",
            "url": "https://novifood.pt/",
            "priceRange": "€€",
            "address": { "@type": "PostalAddress", "addressCountry": "PT" },
            "areaServed": "PT",
        },
    });

    const HERO_IMG = t("hero", "image", HERO_IMG_DEFAULT);
    const ALFA_IMG = t("models_intro", "alfa_image", ALFA_IMG_DEFAULT);
    const OMEGA_IMG = t("models_intro", "omega_image", OMEGA_IMG_DEFAULT);
    const TETO_IMG = t("teto", "image", TETO_IMG_DEFAULT);

    useEffect(() => {
        let alive = true;
        api.get("/testimonials", { params: { featured: true, limit: 3 } })
            .then((r) => {
                if (alive && Array.isArray(r.data) && r.data.length > 0) setTestimonials(r.data);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="home-hero">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 grain-overlay opacity-50" />
                    <div
                        aria-hidden
                        className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full opacity-25"
                        style={{ background: "radial-gradient(circle, #E0AE32 0%, transparent 60%)" }}
                    />
                </div>
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-[0.18em] uppercase text-white/85 backdrop-blur">
                        <Sparkles className="h-3 w-3 text-secondary" />
                        {t("hero", "eyebrow", tr("Um mercado que cresce todos os dias", "A market growing every single day"))}
                    </span>
                    <h1 className="mt-4 display-xl text-white max-w-4xl">
                        {(() => {
                            const raw = t("hero", "title", tr("Revolucione o seu {hl} móvel.", "Revolutionise your mobile {hl}."));
                            const hl = t("hero", "title_highlight", tr("negócio", "business"));
                            if (hl && raw.includes("{hl}")) {
                                const parts = raw.split("{hl}");
                                return <>{parts[0]}<span className="serif-accent text-secondary">{hl}</span>{parts.slice(1).join("{hl}")}</>;
                            }
                            if (hl && raw.includes(hl)) {
                                const parts = raw.split(hl);
                                return <>{parts[0]}<span className="serif-accent text-secondary">{hl}</span>{parts.slice(1).join(hl)}</>;
                            }
                            return raw;
                        })()}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/75 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Soluções móveis e sustentáveis para vender sumos, cafés, gelados e refeições quentes.",
                            "Mobile, sustainable solutions to sell juices, coffee, ice cream and hot meals."
                        ))}
                    </p>
                    <div className="mt-5">
                        <Link to={t("hero", "cta_primary_to", "/simulador")} data-testid="hero-cta-simule"
                            className="btn-premium-gold">
                            {t("hero", "cta_primary_label", tr("Simule os seus ganhos", "Simulate your earnings"))}
                            <ArrowRight className="h-4 w-4 arrow-shift" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="bg-white border-b border-border" data-testid="section-stats">
                <div className="container-x py-8 lg:py-10 grid gap-6 sm:gap-8 md:grid-cols-4 text-primary">
                    <Counter end={300} suffix="+" label={tr("Carrinhos entregues", "Carts delivered")} />
                    <Counter end={15} suffix="" label={tr("Cidades activas", "Active cities")} />
                    <Counter end={92} suffix="%" label={tr("Clientes satisfeitos", "Happy customers")} />
                    <Counter end={2} suffix={tr(" anos", " years")} label={tr("Garantia incluída", "Warranty included")} />
                </div>
            </section>

            {/* BRAND MARQUEE — rotating real cases */}
            <BrandMarquee limit={14} speedSeconds={45} />

            {/* A NOVIFOOD */}
            <section className="container-x section-y" data-testid="section-novifood">
                <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16">
                    <Reveal className="lg:col-span-5">
                        <span className="eyebrow-line">{tr("A NOVIFOOD", "About NOVIFOOD")}</span>
                        <h2 className="mt-5 display-lg text-primary">
                            {tr("Carrinhos ", "")}<span className="serif-accent text-primary">{tr("inovadores", "Innovative")}</span>{tr(" para a comida de rua.", " carts for street food.")}
                        </h2>
                    </Reveal>
                    <Reveal delay={1} className="lg:col-span-7 text-base lg:text-lg text-muted-foreground leading-relaxed">
                        <p>
                            {tr(
                                "A NOVIFOOD concebe carrinhos móveis equipados com arcas congeladas, liquidificadores, máquinas de café e equipamento para comida quente — todos alimentados por painéis solares. Uma forma limpa, elegante e rentável de iniciar o seu negócio onde o público está.",
                                "NOVIFOOD designs mobile carts equipped with freezers, blenders, coffee machines and hot food equipment — all powered by solar panels. A clean, elegant and profitable way to start your business wherever your audience is."
                            )}
                        </p>
                    </Reveal>
                </div>
                <div className="mt-16 grid gap-6 md:grid-cols-3">
                    <Reveal delay={1}><FeatureCard icon={Leaf} title={tr("Versátil", "Versatile")} desc={tr("Vários tipos de produtos e modelos de negócio num mesmo equipamento.", "Multiple product types and business models in a single unit.")} /></Reveal>
                    <Reveal delay={2}><FeatureCard icon={Sun} title={tr("Sustentável", "Sustainable")} desc={tr("Painéis solares integrados para autonomia energética total.", "Integrated solar panels for total energy autonomy.")} highlight /></Reveal>
                    <Reveal delay={3}><FeatureCard icon={TrendingUp} title={tr("Lucrativo", "Profitable")} desc={tr("Margens de lucro entre 60% e 85% em operação normal.", "Profit margins between 60% and 85% in normal operation.")} /></Reveal>
                </div>
                {/* Inline CTA — nudge to /processos */}
                <Reveal delay={2} className="mt-12">
                    <InlineCTA
                        eyebrow={tr("Como fazemos", "How we do it")}
                        title={tr("Vê o processo de construção — do briefing à entrega.", "See our build process — from briefing to delivery.")}
                        to="/processos"
                        label={tr("Ver processos", "See process")}
                        variant="ghost"
                        icon={Sparkles}
                        testId="home-inline-cta-processos"
                    />
                </Reveal>
            </section>

            {/* ILHAS FUNCIONAIS */}
            <section className="bg-muted/60 border-y border-border" data-testid="section-models">
                <div className="container-x section-y">
                    <div className="flex items-end justify-between gap-8 flex-wrap">
                        <Reveal className="max-w-2xl">
                            <span className="eyebrow-line">{tr("As nossas ilhas funcionais", "Our functional islands")}</span>
                            <h2 className="mt-5 display-lg text-primary">
                                {tr("Dois modelos, uma só ", "Two models, one ")}<span className="serif-accent text-primary">{tr("ambição", "ambition")}</span>{tr(": o seu negócio.", ": your business.")}
                            </h2>
                        </Reveal>
                        <Reveal delay={1}>
                            <p className="text-muted-foreground max-w-sm leading-relaxed">
                                {tr("Compactos, robustos e prontos a transformar a sua ideia em receita diária.", "Compact, robust and ready to turn your idea into daily revenue.")}
                            </p>
                        </Reveal>
                    </div>
                    <div className="mt-16 grid gap-8 lg:grid-cols-2">
                        <Reveal delay={1}>
                            <ModelCard testId="model-card-alfa" img={ALFA_IMG} tag={tr("Modelo Compacto", "Compact Model")} name={tr("Modelo Alfa", "Alfa Model")} price={fmtPrice(2000)}
                                blurb={tr("Ideal para cafés, doces e snacks. Compacto, manobrável e pronto a operar em qualquer praça.", "Ideal for coffee, sweets and snacks. Compact, manoeuvrable and ready to operate anywhere.")}
                                to="/modelo-alfa" />
                        </Reveal>
                        <Reveal delay={2}>
                            <ModelCard testId="model-card-omega" img={OMEGA_IMG} tag={tr("Modelo Premium", "Premium Model")} name={tr("Modelo Ômega", "Omega Model")} price={fmtPrice(3000)}
                                blurb={tr("Mais espaço, mais equipamento. Para refeições quentes, gelados e operações de maior volume.", "More space, more equipment. For hot meals, ice cream and higher-volume operations.")}
                                to="/modelo-omega" />
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* COMPARISON */}
            <section className="container-x section-y" data-testid="section-compare">
                <Reveal className="max-w-2xl mb-14">
                    <span className="eyebrow-line">{tr("Comparar modelos", "Compare models")}</span>
                    <h2 className="mt-5 display-lg text-primary">
                        {tr("Qual é o ", "Which one is ")}<span className="serif-accent text-primary">{tr("ideal", "ideal")}</span>{tr(" para si?", " for you?")}
                    </h2>
                    <p className="mt-5 text-muted-foreground leading-relaxed">
                        {tr("Compare as especificações lado a lado. Os ", "Compare specs side by side. The ")}<strong className="text-primary">{tr("badges dourados", "gold badges")}</strong>{tr(" assinalam as vantagens do modelo premium.", " highlight the premium model's advantages.")}
                    </p>
                </Reveal>

                <div className="relative grid gap-6 lg:grid-cols-2">
                    {/* Centre VS pill */}
                    <span className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-lg font-extrabold shadow-lg ring-4 ring-white">
                        VS
                    </span>

                    {/* ALFA */}
                    <SpecCard
                        testId="compare-alfa"
                        tag={tr("Compacto", "Compact")}
                        name={tr("Modelo Alfa", "Alfa Model")}
                        price={fmtPrice(2000)}
                        priceHint={tr("a partir de", "from")}
                        blurb={tr("Ideal para single-product e zonas com espaço limitado.", "Ideal for single-product setups and tight spaces.")}
                        to="/modelo-alfa"
                        specs={[
                            { icon: Ruler, label: tr("Dimensões", "Dimensions"), value: "1,80 × 1,10 m" },
                            { icon: Snowflake, label: tr("Congelador", "Freezer"), value: "120 L" },
                            { icon: Sun, label: tr("Painel solar", "Solar panel"), value: "250 W" },
                            { icon: BatteryCharging, label: tr("Bateria lítio", "Lithium battery"), value: "1 kWh" },
                            { icon: Flame, label: tr("Gás", "Gas"), value: tr("Opcional", "Optional") },
                            { icon: Ruler, label: tr("Balcão útil", "Counter space"), value: "1,2 m" },
                            { icon: Users, label: tr("Operação", "Operation"), value: tr("1 pessoa", "1 person") },
                            { icon: Truck, label: tr("Manobrabilidade", "Manoeuvrability"), value: tr("Máxima", "Maximum") },
                        ]}
                    />

                    {/* OMEGA */}
                    <SpecCard
                        testId="compare-omega"
                        tag="Premium"
                        name={tr("Modelo Ômega", "Omega Model")}
                        price={fmtPrice(3000)}
                        priceHint={tr("a partir de", "from")}
                        blurb={tr("Para refeições quentes, gelados e operações de maior volume.", "For hot meals, ice cream and higher-volume operations.")}
                        to="/modelo-omega"
                        highlighted
                        recommended
                        specs={[
                            { icon: Ruler, label: tr("Dimensões", "Dimensions"), value: "2,20 × 1,30 m", badge: "+22%" },
                            { icon: Snowflake, label: tr("Congelador", "Freezer"), value: "200 L", badge: "+67%" },
                            { icon: Sun, label: tr("Painel solar", "Solar panel"), value: "400 W", badge: "1,6×" },
                            { icon: BatteryCharging, label: tr("Bateria lítio", "Lithium battery"), value: "2 kWh", badge: "2×" },
                            { icon: Flame, label: tr("Gás", "Gas"), value: tr("Incluído", "Included"), badge: "✓" },
                            { icon: Ruler, label: tr("Balcão útil", "Counter space"), value: "1,8 m", badge: "+50%" },
                            { icon: Users, label: tr("Operação", "Operation"), value: tr("1–2 pessoas", "1–2 people"), badge: "2×" },
                            { icon: Award, label: tr("Equipamento", "Equipment"), value: tr("Multi-função", "Multi-purpose") },
                        ]}
                    />
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-muted p-6">
                    <div>
                        <h3 className="font-heading text-xl font-extrabold text-primary">{tr("Ainda na dúvida?", "Still undecided?")}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            {tr(
                                "Configure o seu carrinho passo-a-passo no simulador e receba orçamento personalizado em 48h.",
                                "Configure your cart step by step in the configurator and get a personalised quote within 48h."
                            )}
                        </p>
                    </div>
                    <Link to="/simulador"
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition"
                        data-testid="compare-cta">
                        {tr("Abrir simulador", "Open configurator")} <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>

            {/* NEGÓCIOS EM DESTAQUE (5 flagship layouts with real photos) */}
            <FlagshipSection tr={tr} />

            {/* VERSATIL */}
            <section className="bg-muted/60 border-y border-border" data-testid="section-versatile">
                <div className="container-x section-y-sm">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                        <div className="max-w-2xl">
                            <span className="eyebrow-line">{t("versatile", "eyebrow", tr("+ Possibilidades", "+ More possibilities"))}</span>
                            <h2 className="mt-2 display-lg text-primary">
                                {tr("O carrinho ", "The ")}<span className="serif-accent text-primary">{tr("certo", "right")}</span>{tr(" para qualquer negócio.", " cart for any business.")}
                            </h2>
                            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                                {t("versatile", "subtitle", tr(
                                    "Do café artesanal ao gelado gourmet, dos sumos naturais aos sushis — adapte o seu NOVIFOOD ao público que quer servir.",
                                    "From artisan coffee to gourmet ice cream, fresh juices to sushi — adapt your NOVIFOOD to the audience you want to serve."
                                ))}
                            </p>
                        </div>
                        <Link to={t("versatile", "cta_to", "/simulador")}
                            className="btn-premium-navy shrink-0"
                            data-testid="versatile-cta">
                            {t("versatile", "cta_label", tr("Configurar o meu tema", "Configure my theme"))}
                            <ArrowRight className="h-4 w-4 arrow-shift" />
                        </Link>
                    </div>
                    <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2 sm:gap-2.5" data-testid="possibilities-grid">
                        {themes.map((theme) => (
                            <ThemeCard key={theme.id} theme={theme} />
                        ))}
                    </div>
                </div>
            </section>

            {/* TETO RETRATIL */}
            <TetoRetratilSection tetoFeatures={tetoFeatures} tr={tr} t={t} />

            {/* FUNCIONAL & SUSTENTAVEL */}
            <section className="bg-primary text-white" data-testid="section-functional">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                        <Reveal className="lg:col-span-5">
                            <span className="inline-flex items-center gap-3 text-[10px] sm:text-xs font-bold uppercase tracking-[0.28em] text-secondary">
                                <span aria-hidden className="inline-block h-[1.5px] w-7 bg-secondary" />
                                {t("functional", "eyebrow", tr("Funcional & Sustentável", "Functional & Sustainable"))}
                            </span>
                            <h2 className="mt-5 display-lg text-white">
                                {tr("Tripla ", "Triple ")}<span className="serif-accent text-secondary">{tr("autonomia", "autonomy")}</span>{tr(": sol, luz e gás.", ": sun, grid and gas.")}
                            </h2>
                            <p className="mt-7 text-white/75 text-lg leading-relaxed">
                                {t("functional", "subtitle", tr(
                                    "Cada NOVIFOOD é pensado para resistir ao dia-a-dia de operação em rua.",
                                    "Every NOVIFOOD is built to withstand day-to-day street operation."
                                ))}
                            </p>
                        </Reveal>
                        <Reveal delay={1} className="lg:col-span-7 grid gap-5 sm:grid-cols-3">
                            {functionalTiles.map((tile, i) => (
                                <InfoTile key={`${tile.title}-${i}`} icon={resolveIcon(tile.icon, "Sun")} title={tile.title} desc={tile.desc} />
                            ))}
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MID-PAGE CTA */}
            <section className="container-x pt-16 lg:pt-20" data-testid="section-mid-cta">
                <CtaBanner
                    eyebrow={tr("Faça as contas", "Do the maths")}
                    title={tr("Quanto pode faturar com um NOVIFOOD?", "How much can you earn with a NOVIFOOD?")}
                    description={tr(
                        "Configure o seu modelo, escolha o tema e veja a estimativa de retorno em poucos minutos. Sem compromisso, totalmente online.",
                        "Configure your model, pick a theme and see your estimated return in minutes. No commitment, fully online."
                    )}
                    primary={{ to: "/simulador", label: tr("Abrir simulador", "Open configurator") }}
                    secondary={{ to: "/contacto", label: tr("Falar com especialista", "Talk to a specialist") }}
                    variant="navy"
                    testId="home-mid-cta"
                />
            </section>

            {/* TESTIMONIALS */}
            <section className="container-x section-y" data-testid="section-testimonials">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow-line">{t("testimonials_intro", "eyebrow", tr("Testemunhos", "Testimonials"))}</span>
                    <h2 className="mt-5 display-lg text-primary">
                        {tr("Empreendedores que já ", "Entrepreneurs already ")}<span className="serif-accent text-primary">{tr("rodam", "rolling with")}</span>{tr(" NOVIFOOD.", " NOVIFOOD.")}
                    </h2>
                </Reveal>
                <div className="mt-16 grid gap-6 md:grid-cols-3">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.id || t.name} delay={Math.min(i + 1, 4)}>
                            <figure data-testid={`testimonial-${i}`}
                                className="h-full rounded-3xl border border-border bg-white p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30">
                                <Quote className="h-8 w-8 text-secondary" strokeWidth={1.5} />
                                <blockquote className="mt-5 text-primary/90 leading-relaxed text-[17px] font-light">
                                    &ldquo;{t.quote}&rdquo;
                                </blockquote>
                                <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                                    {t.photo_url && (
                                        <img src={t.photo_url} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-secondary/40" loading="lazy" />
                                    )}
                                    <div>
                                        <div className="font-heading font-bold text-primary">{t.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{t.role}</div>
                                    </div>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CALCULADORA */}
            <ProfitCalculator />

            {/* FINAL CTA */}
            <section className="container-x section-y-sm" data-testid="section-final-cta">
                <Reveal>
                    <div className="relative overflow-hidden rounded-[32px] border border-secondary/40 bg-secondary p-8 sm:p-12 lg:p-16">
                        <div aria-hidden className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-primary/10" />
                        <div aria-hidden className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-white/20" />
                        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <h3 className="display-lg text-primary">
                                    {tr("Pronto para abrir o seu ", "Ready to launch your ")}<span className="serif-accent text-primary">NOVIFOOD</span>?
                                </h3>
                                <p className="mt-5 text-primary/80 max-w-2xl text-lg leading-relaxed">
                                    {t("final_cta", "subtitle", tr(
                                        "Fale connosco e receba uma proposta personalizada em 48h. Sem compromisso.",
                                        "Talk to us and get a personalised proposal within 48h. No commitment."
                                    ))}
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex lg:justify-end">
                                <Link to={t("final_cta", "cta_primary_to", "/contacto")} data-testid="final-cta-link"
                                    className="btn-premium-navy">
                                    {t("final_cta", "cta_primary_label", tr("Pedir orçamento", "Request a quote"))}
                                    <ArrowRight className="h-4 w-4 arrow-shift" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>
        </>
    );
}

function FeatureCard({ icon: Icon, title, desc, highlight }) {
    return (
        <div className={`group relative rounded-2xl border p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 ${
            highlight
                ? "bg-primary text-white border-primary shadow-[0_24px_48px_-16px_rgba(11,37,69,0.35)]"
                : "bg-white border-border hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30"
        }`} data-testid={`feature-card-${title.toLowerCase()}`}>
            <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 ${highlight ? "bg-secondary text-primary" : "bg-muted text-primary"}`}>
                <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-6 font-heading text-2xl lg:text-[28px] font-bold leading-tight">{title}</h3>
            <p className={`mt-3 text-sm lg:text-base leading-relaxed ${highlight ? "text-white/75" : "text-muted-foreground"}`}>{desc}</p>
        </div>
    );
}

function ModelCard({ img, tag, name, price, blurb, to, testId }) {
    const { tr } = useLocale();
    return (
        <Link to={to} data-testid={testId}
            className="group relative overflow-hidden rounded-3xl border border-border bg-white transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_32px_64px_-20px_rgba(11,37,69,0.25)] hover:border-secondary/30">
            <div className="aspect-[16/10] overflow-hidden">
                <img src={img} alt={name}
                    className="h-full w-full object-cover transition duration-1000 ease-out group-hover:scale-[1.06]" />
            </div>
            <div className="p-7 lg:p-8">
                <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex rounded-full bg-muted px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-primary/70">{tag}</span>
                    <span className="font-heading text-2xl font-extrabold text-primary">
                        <span className="serif-accent">{price}</span>
                    </span>
                </div>
                <h3 className="mt-5 font-heading text-3xl lg:text-4xl font-extrabold text-primary leading-tight">{name}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{blurb}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-primary">
                    {tr("Ver especificações", "View specifications")}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
            </div>
        </Link>
    );
}

function InfoTile({ icon: Icon, title, desc }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 lg:p-7 backdrop-blur transition-all duration-500 ease-out hover:bg-white/10 hover:-translate-y-1 hover:border-secondary/30">
            <Icon className="h-6 w-6 text-secondary" strokeWidth={1.75} />
            <h4 className="mt-5 font-heading text-lg font-bold">{title}</h4>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</p>
        </div>
    );
}

function FeatureRow({ icon: Icon, title, desc }) {
    return (
        <div className="flex items-start gap-5 rounded-2xl border border-border bg-white p-5 lg:p-6 transition-all duration-500 ease-out hover:-translate-y-0.5 hover:shadow-md hover:border-secondary/30">
            <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/15 text-primary ring-1 ring-secondary/20">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
                <h4 className="font-heading text-lg font-bold text-primary">{title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">{desc}</p>
            </div>
        </div>
    );
}


/* ---------- FlagshipSection: wraps toggle + grid ---------- */
function FlagshipSection({ tr }) {
    const [branded, setBranded] = useState(false);
    return (
        <section className="container-x section-y" data-testid="section-flagship">
            <Reveal>
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-3xl">
                        <span className="eyebrow-line">{tr("Negócios em destaque", "Featured businesses")}</span>
                        <h2 className="mt-5 display-lg text-primary">
                            {tr("5 configurações ", "5 configurations ")}<span className="serif-accent text-primary">{tr("prontas", "ready")}</span>{tr(" para arrancar.", " to launch.")}
                        </h2>
                        <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                            {tr(
                                "Layouts recomendados por tipo de negócio — cada um pensado com o equipamento, cores e cenário certos. Escolha ver o carrinho sem marca (mockup) ou com um exemplo de branding aplicado.",
                                "Recommended layouts by business type — each designed with the right equipment, colours and setting. Toggle between the blank mockup and an example branded version."
                            )}
                        </p>
                    </div>
                    {/* Toggle */}
                    <div className="inline-flex rounded-full border border-border bg-white p-1 shadow-sm" role="tablist" aria-label={tr("Alternar versão", "Toggle version")}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={!branded}
                            data-testid="flagship-toggle-clean"
                            onClick={() => setBranded(false)}
                            className={`px-4 py-2 text-xs font-bold rounded-full transition ${!branded ? "bg-primary text-white shadow" : "text-primary/70 hover:text-primary"}`}
                        >
                            {tr("Sem marca", "Blank")}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={branded}
                            data-testid="flagship-toggle-branded"
                            onClick={() => setBranded(true)}
                            className={`px-4 py-2 text-xs font-bold rounded-full transition ${branded ? "bg-primary text-white shadow" : "text-primary/70 hover:text-primary"}`}
                        >
                            {tr("Com marca", "Branded")}
                        </button>
                    </div>
                </div>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="flagship-grid">
                {FLAGSHIP_BUSINESSES.map((biz, i) => (
                    <Reveal key={biz.id} delay={Math.min(i + 1, 4)}>
                        <FlagshipCard biz={biz} branded={branded} tr={tr} />
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

/* ---------- FlagshipCard: photo-based business layout ---------- */
function FlagshipCard({ biz, branded, tr }) {
    const equip = tr(biz.equip_pt, biz.equip_en);
    const pitch = tr(biz.pitch_pt, biz.pitch_en);
    const marginHint = tr(biz.margin_hint_pt, biz.margin_hint_en);
    const imgSrc = branded ? biz.image_branded : biz.image_clean;
    const linkTo = `/simulador?tema=${encodeURIComponent(biz.tema)}${biz.preset_brand ? `&brand=${encodeURIComponent(biz.preset_brand)}` : ""}`;
    return (
        <Link
            to={linkTo}
            data-testid={`flagship-${biz.id}`}
            className="group relative flex flex-col rounded-3xl border border-border bg-white overflow-hidden transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_32px_64px_-20px_rgba(11,37,69,0.25)] hover:border-secondary/40"
        >
            <div className="aspect-[3/2] overflow-hidden bg-muted relative">
                <img
                    key={imgSrc}
                    src={imgSrc}
                    alt={biz.label}
                    className="h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.05]"
                    loading="lazy"
                />
                {branded ? (
                    <span className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-primary shadow-sm">
                        {tr("Exemplo:", "Example:")} {biz.brand_example}
                    </span>
                ) : (
                    <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-primary/70 shadow-sm">
                        {tr("Mockup neutro", "Blank mockup")}
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col p-6 lg:p-7">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-2xl font-extrabold text-primary leading-tight">
                        {biz.label}
                    </h3>
                    <div className="text-right shrink-0">
                        <div className="font-heading text-lg font-extrabold text-primary">
                            <span className="serif-accent">{biz.margin}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">
                            {marginHint}
                        </div>
                    </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{pitch}</p>
                <ul className="mt-5 space-y-1.5 text-xs">
                    {equip.map((e) => (
                        <li key={e} className="flex items-center gap-2 text-primary/80">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-secondary shrink-0" />
                            {e}
                        </li>
                    ))}
                </ul>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary">
                    {tr("Ver configuração", "See configuration")}
                    <ArrowRight className="h-4 w-4 arrow-shift transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
            </div>
        </Link>
    );
}

/* ---------- ThemeCard: mini-cart SVG per business theme ---------- */
function ThemeCard({ theme }) {
    const { en } = useLocale();
    const Icon = typeof theme.icon === "string" ? resolveIcon(theme.icon, "Sparkles") : theme.icon;
    const label = en ? (THEME_LABEL_EN[theme.id] || theme.label) : theme.label;
    return (
        <Link
            to={`/negocios/${encodeURIComponent(theme.id)}`}
            className="group relative flex flex-col gap-1 rounded-xl border border-border bg-white p-2 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(11,37,69,0.18)] hover:border-secondary/40"
            data-testid={`possibility-${theme.id}`}
            style={{ background: `linear-gradient(180deg, ${theme.soft} 0%, #FFFFFF 60%)` }}
            aria-label={`Ver landing page do nicho ${theme.label}`}
        >
            <ThemeMiniCart theme={theme} IconComponent={Icon} />
            <div className="flex items-center gap-1 pt-0.5">
                <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded"
                    style={{ background: theme.primary, color: "#FFFFFF" }}
                >
                    <Icon className="h-2.5 w-2.5" />
                </span>
                <span className="font-heading text-[11px] font-extrabold text-primary truncate">{label}</span>
            </div>
        </Link>
    );
}

function ThemeMiniCart({ theme, IconComponent }) {
    // ViewBox 200x140. Striped awning + body with brand label + primary icon on counter + 2 wheels
    const patternId = `stripe-${theme.id}`;
    const Icon = IconComponent || (typeof theme.icon === "string" ? resolveIcon(theme.icon, "Sparkles") : theme.icon);
    return (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" aria-hidden="true">
            <defs>
                <pattern id={patternId} patternUnits="userSpaceOnUse" width="14" height="14">
                    <rect width="14" height="14" fill={theme.primary} />
                    <rect x="7" width="7" height="14" fill={theme.accent} />
                </pattern>
                <linearGradient id={`body-${theme.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="100%" stopColor="#E4E8EC" />
                </linearGradient>
                <radialGradient id={`shad-${theme.id}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(30,58,95,0.25)" />
                    <stop offset="100%" stopColor="rgba(30,58,95,0)" />
                </radialGradient>
            </defs>

            {/* Ground shadow */}
            <ellipse cx="100" cy="128" rx="68" ry="5" fill={`url(#shad-${theme.id})`} />

            {/* Poles */}
            <rect x="42" y="26" width="2.5" height="54" fill="#1E293B" />
            <rect x="155.5" y="26" width="2.5" height="54" fill="#1E293B" />
            <circle cx="43" cy="26" r="2" fill={theme.primary} />
            <circle cx="157" cy="26" r="2" fill={theme.primary} />

            {/* Awning — angled stripe */}
            <rect x="36" y="22" width="128" height="4" fill="#1E293B" rx="1" />
            <path d="M 34 24 L 166 24 L 172 50 L 28 50 Z" fill={`url(#${patternId})`} stroke="#0F172A" strokeWidth="1" />
            {/* Valance with brand */}
            <rect x="28" y="50" width="144" height="11" fill={theme.accent} stroke="#0F172A" strokeWidth="0.8" />
            <text
                x="100" y="58"
                textAnchor="middle"
                fontSize="7"
                fontWeight="900"
                letterSpacing="1.8"
                fill={["#FBCFE8", "#A5F3FC", "#FED7AA", "#FCD34D", "#FDE68A", "#E9D5FF", "#DBEAFE", "#FECACA"].includes(theme.accent) ? "#0F172A" : "#FFFFFF"}
                style={{ textTransform: "uppercase" }}
            >
                {theme.brand}
            </text>
            {/* Scalloped lower edge */}
            {Array.from({ length: 9 }).map((_, i) => (
                <circle key={i} cx={32 + i * 17} cy="61" r="5" fill={theme.accent} stroke="#0F172A" strokeWidth="0.7" />
            ))}

            {/* Top rack */}
            <rect x="35" y="78" width="130" height="4" fill="#F59E0B" rx="1" />

            {/* Body */}
            <rect x="35" y="82" width="130" height="34" fill={`url(#body-${theme.id})`} stroke={theme.primary} strokeWidth="1.5" rx="2" />

            {/* Counter band */}
            <rect x="37" y="84" width="126" height="9" fill="#1E3A5F" rx="1" />

            {/* Primary equipment icon on counter */}
            <g transform="translate(91, 85)">
                <rect width="18" height="7" rx="1.2" fill={theme.accent} />
                <foreignObject x="2" y="0.5" width="14" height="6">
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: theme.primary }}>
                        <Icon size={5} strokeWidth={2.8} />
                    </div>
                </foreignObject>
            </g>

            {/* Service window */}
            <rect x="46" y="96" width="108" height="14" fill="#BAE6FD" stroke={theme.primary} strokeWidth="0.6" opacity="0.8" rx="1" />
            <path d="M 50 98 L 56 98 L 52 110 L 48 110 Z" fill="rgba(255,255,255,0.6)" />

            {/* Bottom kick */}
            <rect x="35" y="113" width="130" height="3" fill={theme.primary} />

            {/* Wheels */}
            {[50, 150].map((cx) => (
                <g key={cx}>
                    <circle cx={cx} cy="122" r="8" fill="#1E293B" stroke="#0F172A" strokeWidth="0.8" />
                    <circle cx={cx} cy="122" r="5" fill="none" stroke={theme.accent} strokeWidth="0.8" />
                    <circle cx={cx} cy="122" r="1.8" fill={theme.accent} />
                </g>
            ))}
        </svg>
    );
}

/* ---------- SpecCard: side-by-side model comparison ---------- */
function SpecCard({ tag, name, price, priceHint, blurb, specs, to, testId, highlighted, recommended }) {
    const { tr } = useLocale();
    return (
        <div
            className={`relative rounded-2xl border-2 p-6 lg:p-5 sm:p-7 transition hover:-translate-y-1 hover:shadow-xl ${
                highlighted
                    ? "border-secondary bg-primary text-primary-foreground"
                    : "border-border bg-white"
            }`}
            data-testid={testId}
        >
            {recommended && (
                <span className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary shadow-md">
                    <Award className="h-3 w-3" /> {tr("Recomendado", "Recommended")}
                </span>
            )}

            <div className="flex items-baseline justify-between gap-4">
                <span
                    className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
                        highlighted ? "bg-secondary text-primary" : "bg-muted text-primary/70"
                    }`}
                >
                    {tag}
                </span>
                <div className="text-right">
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${highlighted ? "text-white/60" : "text-muted-foreground"}`}>
                        {priceHint}
                    </div>
                    <div className={`font-heading text-3xl font-extrabold leading-none ${highlighted ? "text-secondary" : "text-primary"}`}>
                        {price}
                    </div>
                </div>
            </div>

            <h3 className={`mt-4 font-heading text-3xl lg:text-4xl font-extrabold leading-tight ${highlighted ? "text-white" : "text-primary"}`}>
                {name}
            </h3>
            <p className={`mt-2 text-sm leading-relaxed ${highlighted ? "text-white/75" : "text-muted-foreground"}`}>{blurb}</p>

            <div className="mt-6 grid grid-cols-2 gap-2.5">
                {specs.map((s) => (
                    <div
                        key={s.label}
                        className={`relative rounded-lg p-3 ${
                            highlighted ? "bg-white/10 backdrop-blur-sm" : "bg-muted"
                        }`}
                    >
                        <div className="flex items-center gap-1.5">
                            <s.icon className={`h-3.5 w-3.5 ${highlighted ? "text-secondary" : "text-primary/70"}`} />
                            <span className={`text-[9px] font-bold uppercase tracking-wider ${highlighted ? "text-white/60" : "text-muted-foreground"}`}>
                                {s.label}
                            </span>
                        </div>
                        <div className={`mt-1 font-heading text-sm lg:text-base font-extrabold leading-tight ${highlighted ? "text-white" : "text-primary"}`}>
                            {s.value}
                        </div>
                        {s.badge && (
                            <span className="absolute top-1.5 right-1.5 inline-flex items-center rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-extrabold text-primary leading-none">
                                {s.badge}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <Link
                to={to}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5 ${
                    highlighted ? "bg-secondary text-primary" : "bg-primary text-primary-foreground"
                }`}
            >
                {tr("Ver especificações", "View specifications")} <ArrowRight className="h-4 w-4" />
            </Link>
        </div>
    );
}


/* ────────────────────────────────────────────────────────────────────
 * TetoRetratilSection — animação WebP + som subtil de tecido a dobrar
 * (sync com a animação de 3 s). Áudio arranca mute por causa das políticas
 * de autoplay dos browsers — clique no ícone para activar. Pausa
 * automaticamente quando a secção sai do viewport (poupa CPU + battery).
 * ─────────────────────────────────────────────────────────────────── */
function TetoRetratilSection({ tetoFeatures, tr, t }) {
    const audioRef = useRef(null);
    const sectionRef = useRef(null);
    const [soundOn, setSoundOn] = useState(false);
    const [inView, setInView] = useState(false);

    /* Observer para saber se a secção está visível — só toca quando sim */
    useEffect(() => {
        const el = sectionRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(
            ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio > 0.4),
            { threshold: [0, 0.4, 1] }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    /* Play/pause sync com estado */
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        if (soundOn && inView) {
            a.play().catch(() => {
                /* Browser bloqueou — reverter para OFF silenciosamente */
                setSoundOn(false);
            });
        } else {
            a.pause();
        }
    }, [soundOn, inView]);

    const toggleSound = () => setSoundOn((v) => !v);

    return (
        <section ref={sectionRef} className="container-x section-y" data-testid="section-teto">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <Reveal className="lg:col-span-6 relative">
                    <div className="relative aspect-[16/9] overflow-hidden rounded-3xl border border-border shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] bg-muted">
                        <img
                            src="/brand/teto-retratil-timelapse.webp"
                            alt={tr("Cobertura retrátil a fechar em 30 segundos — animação time-lapse", "Retractable canopy folding in 30 seconds — time-lapse animation")}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            data-testid="teto-timelapse"
                        />
                        {/* Badge time-lapse */}
                        <span className="pointer-events-none absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary shadow-md border border-white">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary" />
                            </span>
                            {tr("Time-lapse · 30 s → 3 s", "Time-lapse · 30 s → 3 s")}
                        </span>
                        {/* Sound toggle */}
                        <button
                            type="button"
                            onClick={toggleSound}
                            aria-label={soundOn ? tr("Desligar som", "Mute sound") : tr("Ligar som", "Unmute sound")}
                            aria-pressed={soundOn}
                            data-testid="teto-sound-toggle"
                            className={`absolute bottom-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white shadow-md backdrop-blur-sm transition-all duration-300 hover:scale-105 ${
                                soundOn
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-white/90 text-primary hover:bg-white"
                            }`}
                        >
                            {soundOn
                                ? <Volume2 className="h-4 w-4" strokeWidth={2.2} />
                                : <VolumeX className="h-4 w-4" strokeWidth={2.2} />}
                        </button>
                        {/* Hidden audio element — WAV loops seamlessly */}
                        <audio
                            ref={audioRef}
                            src="/brand/teto-retratil-timelapse.wav"
                            loop
                            preload="none"
                            data-testid="teto-audio"
                        />
                    </div>
                    <div className="absolute -bottom-6 left-6 hidden sm:flex items-center gap-3 rounded-xl bg-white border border-border px-5 py-3.5 shadow-lg">
                        <ChevronsUpDown className="h-5 w-5 text-secondary" />
                        <span className="text-sm font-semibold text-primary">{tr("Fechado em 30s", "Folded in 30s")}</span>
                    </div>
                </Reveal>
                <Reveal delay={1} className="lg:col-span-6 space-y-7">
                    <span className="eyebrow-line">{t("teto", "eyebrow", tr("Engenharia em detalhe", "Engineering in detail"))}</span>
                    <h2 className="display-lg text-primary">
                        {tr("Teto ", "")}<span className="serif-accent text-primary">{tr("retrátil", "Retractable")}</span>{tr(". Topo aberto. Operação sem fricção.", " roof. Open top. Frictionless operation.")}
                    </h2>
                    <div className="space-y-4">
                        {tetoFeatures.map((f, i) => (
                            <FeatureRow key={`${f.title}-${i}`} icon={resolveIcon(f.icon, "Sparkles")} title={f.title} desc={f.desc} />
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
